import { CONTACT, LEGAL_ENTITY, SITE } from '@/constants/site.constants';
import type { LegalDocumentContent } from '@/types/legal.types';

/**
 * General Terms & Conditions for the Shoutt platform, as approved by counsel.
 * Rentals-specific clauses live in rentalTerms.constants.ts and take precedence
 * over these terms wherever the two conflict.
 */
export const TERMS_CONTENT: LegalDocumentContent = {
  title: 'General Terms & Conditions',
  summary: `These terms govern your access to and use of the ${SITE.name} website, mobile application, platform and related services operated by ${LEGAL_ENTITY.name}.`,
  sections: [
    {
      id: 'acceptance',
      heading: '1. Acceptance and Scope',
      paragraphs: [
        `These General Terms and Conditions ("Terms") govern your access to and use of the ${SITE.name} website, mobile application, platform, and related services (collectively, the "Platform") operated by ${LEGAL_ENTITY.name} ("Shoutt", "Company", "we", "us", or "our").`,
        'By accessing, registering on, or using the Platform, you ("User", "you", or "your") agree to these Terms, our Privacy Policy, and any additional terms or policies expressly incorporated into these Terms. If you do not agree, you must not use the Platform.',
        'Certain services, including Rentals, may be subject to additional terms. If there is a conflict between these Terms and service-specific terms, the service-specific terms will apply to the extent of that conflict.',
      ],
    },
    {
      id: 'eligibility',
      heading: '2. Eligibility and Accounts',
      bullets: [
        'You must be at least 18 years of age and legally competent to enter into a binding contract to use the Platform, unless a service-specific term expressly provides otherwise and applicable law permits such use.',
        'You are responsible for providing accurate, complete, and current information when creating or maintaining an Account.',
        'You are responsible for maintaining the confidentiality and security of your Account credentials and for activities conducted through your Account. You must promptly notify Shoutt of any unauthorised use or suspected compromise of your Account.',
        'You must not create an Account using false information, impersonate another person, or create multiple Accounts for fraudulent, abusive, or deceptive purposes.',
      ],
    },
    {
      id: 'nature-of-platform',
      heading: '3. Nature and Role of the Platform',
      paragraphs: [
        'Shoutt operates a technology platform that may enable Users to discover, communicate about, list, book, rent, purchase, or otherwise transact in relation to goods or services offered through the Platform, depending on the feature used.',
        'Except where expressly stated otherwise, Shoutt is not the owner, seller, lessor, manufacturer, supplier, insurer, or guarantor of third-party goods or services made available by Users or other third parties through the Platform.',
        'Where a transaction is between Users, the relevant Users are responsible for the terms, performance, legality, and fulfilment of that transaction. Shoutt may provide technology, communications, payment facilitation, support, and administrative services as expressly described in the applicable service terms.',
        'Shoutt does not represent that every listing, User, product, service, or transaction will be independently verified or pre-screened.',
      ],
    },
    {
      id: 'user-content',
      heading: '4. User Content and Listings',
      bullets: [
        'Users are responsible for the accuracy, legality, completeness, and authenticity of information, listings, photographs, descriptions, reviews, messages, and other content they submit to the Platform ("User Content").',
        'By submitting User Content, you represent that you have the rights and authority necessary to submit and use that content and that it does not violate applicable law or the rights of any third party.',
        'You grant Shoutt a non-exclusive, worldwide, royalty-free licence to host, store, reproduce, display, transmit, adapt, and use User Content to the extent reasonably necessary to operate, provide, improve, secure, and promote the Platform, subject to applicable law and our Privacy Policy.',
        'Shoutt may remove, restrict, or disable User Content that it reasonably believes violates these Terms, applicable law, or the rights or safety of Users or third parties.',
      ],
    },
    {
      id: 'user-conduct',
      heading: '5. User Conduct',
      paragraphs: [
        'You must use the Platform lawfully, honestly, and only for its intended purposes.',
        'You must not: (a) engage in fraud, scams, phishing, impersonation, or deceptive conduct; (b) create fake or misleading listings or reviews; (c) manipulate ratings or reviews; (d) use another User’s Account without authorisation; (e) interfere with the security or operation of the Platform; (f) attempt to obtain unauthorised access to systems, accounts, or data; (g) circumvent Platform fees or protections through prohibited off-platform transactions; (h) upload malicious code or harmful material; (i) use the Platform for unlawful, prohibited, or restricted goods or services; or (j) otherwise misuse the Platform.',
        'You must comply with any additional rules, safety requirements, or service-specific terms applicable to the feature you use.',
      ],
    },
    {
      id: 'prohibited-goods',
      heading: '6. Prohibited and Restricted Goods or Activities',
      bullets: [
        'You must not list, offer, sell, rent, distribute, or facilitate any transaction involving goods or activities that are illegal, prohibited, or restricted under applicable law or Shoutt policy.',
        'Shoutt may publish prohibited-item and restricted-activity guidelines. Users remain responsible for determining whether their conduct and any goods or services they offer comply with applicable law.',
        'If Shoutt becomes aware of a prohibited or unlawful listing or activity, it may remove or disable the relevant content, cancel or prevent a transaction, restrict access, suspend or terminate an Account, and take other lawful action.',
      ],
    },
    {
      id: 'reviews',
      heading: '7. Reviews, Ratings, and Feedback',
      bullets: [
        'Reviews, ratings, and feedback must be genuine, relevant, and based on the User’s actual experience.',
        'Users must not manipulate, fabricate, purchase, exchange, threaten, or otherwise improperly influence reviews or ratings.',
        'Shoutt may moderate, remove, restrict, or decline to publish content that violates these Terms, applicable law, or applicable review guidelines.',
      ],
    },
    {
      id: 'intellectual-property',
      heading: '8. Intellectual Property',
      bullets: [
        'The Platform and its software, design, trademarks, logos, text, graphics, interfaces, and other materials provided by Shoutt are owned by or licensed to Shoutt and are protected by applicable intellectual-property laws.',
        'Subject to these Terms, Shoutt grants you a limited, non-exclusive, non-transferable, revocable licence to access and use the Platform for its intended purposes.',
        'You must not copy, modify, distribute, reverse engineer, decompile, scrape, sell, license, or commercially exploit Shoutt’s proprietary materials except as permitted by applicable law or with Shoutt’s prior written consent.',
      ],
    },
    {
      id: 'privacy',
      heading: '9. Privacy and Personal Data',
      bullets: [
        'Shoutt’s collection, use, storage, disclosure, retention, and other processing of personal data are governed by the Shoutt Privacy Policy and applicable data-protection law.',
        'Users are responsible for providing information that is accurate and lawful to provide. Shoutt may process personal data to provide the Platform, facilitate transactions, maintain security, comply with legal obligations, prevent fraud and abuse, and for other purposes described in the Privacy Policy and applicable notices.',
        'Where applicable, Users may exercise rights available to them under applicable data-protection law through the mechanism specified in the Privacy Policy.',
        'The Terms do not replace any notice, consent mechanism, or other requirement that applicable data-protection law requires Shoutt to provide separately.',
      ],
    },
    {
      id: 'disclaimers',
      heading: '10. Disclaimers',
      paragraphs: [
        'To the maximum extent permitted by applicable law, the Platform is provided on an "as-is" and "as-available" basis.',
        'To the maximum extent permitted by applicable law, Shoutt disclaims warranties that cannot lawfully be excluded, including implied warranties concerning third-party goods, services, Users, listings, availability, suitability, or fitness for a particular purpose.',
        'Nothing in these Terms excludes or limits any liability, warranty, right, or remedy that cannot lawfully be excluded or limited.',
      ],
    },
    {
      id: 'indemnification',
      heading: '11. Indemnification',
      paragraphs: [
        `To the maximum extent permitted by applicable law, you agree to indemnify and hold harmless Shoutt, ${LEGAL_ENTITY.name}, and their directors, officers, employees, affiliates, and contractors from third-party claims, losses, damages, liabilities, costs, and reasonable legal expenses arising out of or relating to: (a) your breach of these Terms; (b) your User Content or listing; (c) your unlawful, fraudulent, negligent, or abusive conduct; (d) your use of goods or services you obtain through the Platform; or (e) your violation of applicable law or third-party rights.`,
        'This indemnity does not require you to indemnify Shoutt for Shoutt’s own liability to the extent that such liability cannot lawfully be transferred or excluded.',
      ],
    },
    {
      id: 'suspension',
      heading: '12. Suspension, Termination, and Enforcement',
      bullets: [
        'Shoutt may suspend, restrict, or terminate access to the Platform or an Account where it reasonably believes that a User has violated these Terms, engaged in fraud or abuse, created a safety or legal risk, failed to comply with applicable law, or materially harmed the Platform or another person.',
        'Where appropriate and subject to applicable law, Shoutt may cancel or prevent transactions, remove content, restrict features, withhold pending payouts in accordance with applicable payment rules, preserve relevant records, and cooperate with competent authorities.',
        'Where reasonably practicable, Shoutt may provide notice of suspension or termination and, where appropriate, information about the basis for the action, subject to security, legal, fraud-prevention, and operational considerations.',
        'Suspension or termination does not automatically extinguish obligations that accrued before termination or obligations that by their nature are intended to survive, including payment, indemnification, confidentiality, intellectual-property, dispute, and liability provisions.',
      ],
    },
    {
      id: 'user-disputes',
      heading: '13. User-to-User Disputes and Administrative Review',
      paragraphs: [
        'Where a transaction is between Users, disputes concerning the underlying transaction, goods, services, possession, condition, damage, loss, payment, or performance are primarily between the relevant Users.',
        'Shoutt may provide a support or administrative review process for claims, refunds, payments, deposits, or other Platform functions where applicable. Such a review is an administrative Platform function and is not, by itself, arbitration, mediation, or a final adjudication of the parties’ underlying legal rights.',
        'Users retain any rights and remedies available under applicable law.',
      ],
    },
    {
      id: 'grievance',
      heading: '14. Grievance Redressal',
      paragraphs: [
        'Shoutt shall provide a mechanism through which Users may submit grievances relating to the Platform, Shoutt’s services, payments, refunds, support, or other matters concerning services provided by Shoutt, in accordance with applicable law.',
        'The applicable grievance contact details and mechanism shall be prominently made available on the Platform and may be updated from time to time.',
      ],
      bullets: [
        `Grievance Officer: ${LEGAL_ENTITY.grievanceOfficer}`,
        `Designation: ${LEGAL_ENTITY.grievanceOfficerDesignation}`,
        `Email: ${CONTACT.grievanceEmail}`,
        'The grievance mechanism does not convert Shoutt into a party to an underlying User-to-User transaction and does not, by itself, constitute arbitration or mediation.',
      ],
    },
    {
      id: 'electronic-communications',
      heading: '15. Electronic Communications',
      paragraphs: [
        'By using the Platform, you consent to receiving electronic communications from Shoutt relating to your Account, transactions, security, service updates, policies, and other operational matters, subject to applicable law and your applicable communication preferences.',
        'Electronic records and communications may be used to evidence transactions and notices to the extent permitted by applicable law.',
      ],
    },
    {
      id: 'third-party-services',
      heading: '16. Third-Party Services and Links',
      paragraphs: [
        'The Platform may contain links to or integrations with third-party websites, applications, payment providers, logistics providers, or other services. Shoutt does not control third-party services and is not responsible for their independent acts, omissions, terms, or policies, except to the extent applicable law provides otherwise.',
        'Your use of third-party services may be subject to the third party’s own terms and privacy policies.',
      ],
    },
    {
      id: 'force-majeure',
      heading: '17. Force Majeure',
      paragraphs: [
        'To the maximum extent permitted by applicable law, Shoutt shall not be liable for failure or delay caused by events beyond its reasonable control, including natural disasters, strikes, internet or telecommunications failures, power outages, government action, war, civil disturbance, epidemics, or comparable events.',
      ],
    },
    {
      id: 'governing-law',
      heading: '18. Governing Law and Jurisdiction',
      paragraphs: [
        'These Terms are governed by the laws of India.',
        `Subject to any mandatory statutory rights, consumer forums, or other forums available under applicable law, disputes arising out of or in connection with these Terms shall be subject to the jurisdiction of the competent courts at ${LEGAL_ENTITY.jurisdiction}.`,
      ],
    },
    {
      id: 'changes',
      heading: '19. Changes to These Terms',
      paragraphs: [
        'Shoutt may amend these Terms from time to time. Material changes will be notified through the Platform or another reasonable method before they take effect where required or appropriate.',
        'Changes will apply prospectively and will not alter the terms of a transaction already confirmed before the effective date, except where required by applicable law or expressly agreed by the relevant parties.',
        'Your continued use of the Platform after the effective date of revised Terms constitutes acceptance of the revised Terms to the extent permitted by applicable law.',
      ],
    },
    {
      id: 'severability',
      heading: '20. Severability',
      paragraphs: [
        'If any provision of these Terms is held to be invalid, unlawful, or unenforceable, that provision shall be interpreted or modified to the minimum extent necessary to make it enforceable, where legally permissible, and the remaining provisions shall continue in full force and effect.',
      ],
    },
    {
      id: 'entire-agreement',
      heading: '21. Entire Agreement and No Waiver',
      paragraphs: [
        'These Terms, together with any applicable service-specific terms, policies, and the Privacy Policy expressly incorporated into them, constitute the agreement governing your use of the Platform, subject to any separate written agreement that expressly applies to you.',
        'Shoutt’s failure to enforce any provision of these Terms shall not constitute a waiver of its right to enforce that provision later.',
      ],
    },
    {
      id: 'contact',
      heading: '22. Contact and Legal Notices',
      paragraphs: [
        'Legal and operational notices to Shoutt may be sent to the official contact details published on the Platform or otherwise designated by Shoutt.',
      ],
      bullets: [
        `Company: ${LEGAL_ENTITY.name}`,
        `Platform/Brand: ${SITE.name}`,
        `Registered Office: ${LEGAL_ENTITY.address}`,
        `Email: ${CONTACT.email}`,
      ],
    },
    {
      id: 'acceptance-confirmation',
      heading: '23. Acceptance',
      paragraphs: [
        'By accessing, registering on, or using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms, the applicable service-specific terms, and the Privacy Policy.',
      ],
    },
  ],
};
